import { useAppDispatch, useAppSelector } from './statehooks';

import { getOptionPrices, getVehiclePricing } from '@services/index';

import { updatePrice } from '@store/reducers/vehicleprice';
import { updateVehicleConfigurationPrices } from '@store/reducers/vehicleconfiguration';
import { updateSelectedOptionPrices } from '@store/reducers/selectedoptions';
import { updateFinalisedOptionPrices } from '@store/reducers/finalisedoptions';

import { updateInitialPayment } from '@store/reducers/vehiclesearchparams';
import { forkJoin, Observable, tap } from 'rxjs';
import { setFinished } from '@store/reducers/pricingState';
import { PricingType } from '../enums';

export const usePricing = () => {
  const searchParams = useAppSelector((state) => state.vehicleSearchParams);
  const dispatch = useAppDispatch();

  const asyncWrapper = (observable: Observable<any>) => {
    dispatch(setFinished(false));
    observable.subscribe({
      complete: () => {
        dispatch(setFinished(true));
      },
    });
  };

  const getVehiclePricingAndUpdate = (
    pricingType: PricingType | null = null
  ) => {
    return getVehiclePricing(
      searchParams.id,
      searchParams.duration,
      searchParams.include_maintenance,
      searchParams.initial_payment,
      searchParams.yearly_mileage,
      searchParams.optionsSelected!,
      searchParams.optionsAdded!,
      pricingType
    ).pipe(
      tap((t) => {
        dispatch(updatePrice(t));

        if (t.initialPayment.default !== searchParams.initial_payment) {
          dispatch(updateInitialPayment(t.initialPayment.default));
        }
      })
    );
  };

  const updatePricing = (pricingType: PricingType | null = null) => {
    asyncWrapper(getVehiclePricingAndUpdate(pricingType));
  };

  const getOptionsPricesAndUpdate = (pricingType: PricingType | null) => {
    return getOptionPrices(
      searchParams.id,
      searchParams.duration,
      searchParams.include_maintenance,
      searchParams.initial_payment,
      searchParams.yearly_mileage,
      searchParams.optionsSelected,
      searchParams.optionsAdded,
      pricingType
    ).pipe(
      tap((t) => {
        dispatch(updateVehicleConfigurationPrices(t));
        dispatch(updateSelectedOptionPrices(t));
        dispatch(updateFinalisedOptionPrices(t));
      })
    );
  };

  const updateAllPricing = (pricingType: PricingType | null = null) => {
    asyncWrapper(
      forkJoin({
        pricing: getVehiclePricingAndUpdate(pricingType),
        options: getOptionsPricesAndUpdate(pricingType),
      })
    );
  };

  const updatePricingWithOptions = (
    optionsSelected: number[] | null,
    optionsAdded: number | null,
    pricingType: PricingType | null = null
  ) => {
    asyncWrapper(
      getVehiclePricing(
        searchParams.id,
        searchParams.duration,
        searchParams.include_maintenance,
        searchParams.initial_payment,
        searchParams.yearly_mileage,
        optionsSelected!,
        optionsAdded!,
        pricingType
      ).pipe(tap((t) => dispatch(updatePrice(t))))
    );
  };

  return {
    updatePricing,
    updateAllPricing,
    updatePricingWithOptions,
  };
};
